import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@material-ui/core';

import { api } from '../../services';
import { GERAR_PACOTE_FISCAL_HABILITADO } from '../../utils/constants';
import {
  criarIngestao,
  criarPacote,
  usePermissoesRelatoriosFiscais,
} from './acoes';

// Dialog "Gerar" (plano §6.11): passo 1 tipo, passo 2 competência/período (+
// Forçado com confirmação digitada), passo 3 confirmação com avisos.
const PASSOS = ['Tipo', 'Parâmetros', 'Confirmação'];
const ANO_MINIMO = 2024;
const JANELA_MAX_DIAS = 62;
const NOMES_MES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const TIPO_PACOTE = 'pacote_mensal';
const TIPO_INGESTAO = 'ingestao';

// Com a geração de pacote desligada (GERAR_PACOTE_FISCAL_HABILITADO), o diálogo
// só oferece a reingestão de período.
const tipoPermitido = (tipo) =>
  !GERAR_PACOTE_FISCAL_HABILITADO && tipo === TIPO_PACOTE
    ? TIPO_INGESTAO
    : tipo;

function formatarISO(data) {
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${data.getFullYear()}-${mes}-${dia}`;
}

function diasEntre(inicio, fim) {
  return Math.round((new Date(fim) - new Date(inicio)) / 86400000) + 1;
}

function codificar(objeto) {
  return encodeURIComponent(JSON.stringify(objeto));
}

async function buscarAvisosPacote(ano, mes) {
  const avisos = [];
  const hoje = new Date();

  if (hoje.getFullYear() === ano && hoje.getMonth() + 1 === mes) {
    avisos.push(
      'Competência em andamento: o pacote será gerado como PARCIAL (só até ontem).',
    );
  }

  const periodo = {
    start: new Date(ano, mes - 1, 1).toISOString(),
    end: new Date(ano, mes, 0, 23, 59, 59).toISOString(),
  };

  const pendencias = await api.get(
    `/relatorios_fiscais_pendencias?filter=${codificar({
      aberta: true,
      data_pasta: periodo,
    })}&sort=${codificar(['id', 'ASC'])}&range=${codificar([1, 1])}&nested=[]`,
  );
  const totalPendencias = pendencias.data?.total || 0;
  if (totalPendencias > 0) {
    avisos.push(
      `Há ${totalPendencias} pendência(s) fiscal(is) aberta(s) nesta competência — o pacote sai "com pendências".`,
    );
  }

  const execucoes = await api.get(
    `/relatorios_fiscais_execucoes?filter=${codificar({
      tipo: TIPO_PACOTE,
      competencia_ano: ano,
      competencia_mes: mes,
    })}&sort=${codificar(['id', 'DESC'])}&range=${codificar([1, 10])}`,
  );
  const vigente = (execucoes.data?.data || []).find((execucao) =>
    ['sucesso', 'sucesso_com_pendencias'].includes(execucao.status),
  );
  if (vigente) {
    avisos.push(
      `Já existe um pacote vigente desta competência gerado em ${new Date(
        vigente.finalizado_em,
      ).toLocaleDateString('pt-BR')} (execução #${
        vigente.id
      }). Este será uma nova versão.`,
    );
  }

  return avisos;
}

const DialogoGerar = ({
  open,
  onClose,
  onCriada,
  tipoInicial = TIPO_PACOTE,
  anoInicial,
  mesInicial,
  periodoInicial,
}) => {
  const { podeForcar } = usePermissoesRelatoriosFiscais();
  const hoje = new Date();

  const [passo, setPasso] = useState(0);
  const [tipo, setTipo] = useState(tipoPermitido(tipoInicial));
  const [ano, setAno] = useState(anoInicial || hoje.getFullYear());
  const [mes, setMes] = useState(mesInicial || hoje.getMonth() + 1);
  const [dataInicio, setDataInicio] = useState(periodoInicial?.inicio || '');
  const [dataFim, setDataFim] = useState(periodoInicial?.fim || '');
  const [forcado, setForcado] = useState(false);
  const [confirmacaoForcar, setConfirmacaoForcar] = useState('');
  const [avisos, setAvisos] = useState([]);
  const [carregandoAvisos, setCarregandoAvisos] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPasso(0);
    setTipo(tipoPermitido(tipoInicial));
    setAno(anoInicial || new Date().getFullYear());
    setMes(mesInicial || new Date().getMonth() + 1);
    setDataInicio(periodoInicial?.inicio || '');
    setDataFim(periodoInicial?.fim || '');
    setForcado(false);
    setConfirmacaoForcar('');
    setAvisos([]);
    setErro(null);
  }, [open]);

  function validarParametros() {
    if (tipo === TIPO_PACOTE) {
      if (!ano || ano < ANO_MINIMO || ano > hoje.getFullYear()) {
        return `Ano inválido (entre ${ANO_MINIMO} e ${hoje.getFullYear()}).`;
      }
      if (ano === hoje.getFullYear() && mes > hoje.getMonth() + 1) {
        return 'A competência ainda não começou.';
      }
      return null;
    }

    if (!dataInicio || !dataFim) return 'Informe a data inicial e a final.';
    if (dataInicio > dataFim) {
      return 'A data inicial não pode ser depois da final.';
    }
    if (dataFim > formatarISO(hoje)) return 'A data final não pode ser futura.';
    if (diasEntre(dataInicio, dataFim) > JANELA_MAX_DIAS) {
      return `Período máximo de ${JANELA_MAX_DIAS} dias.`;
    }
    if (forcado && confirmacaoForcar !== 'FORCAR') {
      return 'Digite FORCAR para confirmar o reprocessamento forçado.';
    }
    return null;
  }

  const erroParametros = passo === 1 ? validarParametros() : null;

  async function avancar() {
    setErro(null);

    if (passo === 1) {
      setCarregandoAvisos(true);
      setPasso(2);
      try {
        if (tipo === TIPO_PACOTE) {
          setAvisos(await buscarAvisosPacote(ano, mes));
        } else {
          setAvisos(
            forcado
              ? [
                  'Modo FORÇADO: as notas do período são apagadas e reinseridas a partir dos XMLs.',
                ]
              : [],
          );
        }
      } catch (e) {
        // Avisos são informativos: falha aqui não impede de confirmar.
        setAvisos(['Não foi possível verificar os avisos desta solicitação.']);
      } finally {
        setCarregandoAvisos(false);
      }
      return;
    }

    setPasso(passo + 1);
  }

  async function confirmar() {
    setEnviando(true);
    setErro(null);
    try {
      const dados =
        tipo === TIPO_PACOTE
          ? await criarPacote({ ano, mes })
          : await criarIngestao({ dataInicio, dataFim, forcado });
      onClose();
      if (onCriada) onCriada(dados.execucao_id);
    } catch (e) {
      setErro({ message: e.message, execucaoId: e.dados?.execucao_id });
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={enviando ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Gerar relatório fiscal</DialogTitle>

      <Stepper activeStep={passo} alternativeLabel>
        {PASSOS.map((rotulo) => (
          <Step key={rotulo}>
            <StepLabel>{rotulo}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <DialogContent dividers>
        {passo === 0 && (
          <RadioGroup value={tipo} onChange={(e) => setTipo(e.target.value)}>
            {GERAR_PACOTE_FISCAL_HABILITADO && (
              <FormControlLabel
                value={TIPO_PACOTE}
                control={<Radio color="primary" />}
                label="Pacote mensal (XMLs, PDFs, SPED e ZIP da competência)"
              />
            )}
            <FormControlLabel
              value={TIPO_INGESTAO}
              control={<Radio color="primary" />}
              label="Reingestão de período (relê os XMLs das pastas)"
            />
          </RadioGroup>
        )}

        {passo === 1 && tipo === TIPO_PACOTE && (
          <Box display="flex" style={{ gap: 16 }}>
            <Select
              value={mes}
              onChange={(e) => setMes(Number(e.target.value))}
            >
              {NOMES_MES.map((nome, index) => (
                <MenuItem key={nome} value={index + 1}>
                  {nome}
                </MenuItem>
              ))}
            </Select>
            <TextField
              type="number"
              label="Ano"
              value={ano}
              onChange={(e) => setAno(Number(e.target.value))}
              style={{ width: 100 }}
            />
          </Box>
        )}

        {passo === 1 && tipo === TIPO_INGESTAO && (
          <>
            <Box display="flex" style={{ gap: 16 }}>
              <TextField
                type="date"
                label="Data inicial"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                label="Data final"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Typography variant="caption" color="textSecondary">
              Máximo de {JANELA_MAX_DIAS} dias.
            </Typography>

            {podeForcar && (
              <Box marginTop={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={forcado}
                      onChange={(e) => setForcado(e.target.checked)}
                    />
                  }
                  label="Forçado (apaga e reinsere as notas do período)"
                />
                {forcado && (
                  <TextField
                    fullWidth
                    label='Digite "FORCAR" para confirmar'
                    value={confirmacaoForcar}
                    onChange={(e) => setConfirmacaoForcar(e.target.value)}
                  />
                )}
              </Box>
            )}
          </>
        )}

        {passo === 2 && (
          <>
            <Typography gutterBottom>
              {tipo === TIPO_PACOTE
                ? `Gerar o pacote mensal de ${String(mes).padStart(
                    2,
                    '0',
                  )}/${ano}.`
                : `Reingerir de ${new Date(
                    `${dataInicio}T00:00:00`,
                  ).toLocaleDateString('pt-BR')} a ${new Date(
                    `${dataFim}T00:00:00`,
                  ).toLocaleDateString('pt-BR')}${
                    forcado ? ' (FORÇADO)' : ''
                  }.`}
            </Typography>

            {carregandoAvisos && (
              <Typography color="textSecondary">
                Verificando avisos...
              </Typography>
            )}
            {avisos.map((aviso) => (
              <Typography
                key={aviso}
                variant="body2"
                style={{ color: '#ed6c02', marginBottom: 4 }}
              >
                • {aviso}
              </Typography>
            ))}
          </>
        )}

        {(erroParametros || erro) && (
          <Box marginTop={2}>
            <Typography variant="body2" style={{ color: '#d32f2f' }}>
              {erroParametros || erro.message}
            </Typography>
            {erro?.execucaoId && (
              <Button
                size="small"
                onClick={() => {
                  onClose();
                  if (onCriada) onCriada(erro.execucaoId);
                }}
              >
                Ver execução #{erro.execucaoId}
              </Button>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={enviando}>
          Cancelar
        </Button>
        {passo > 0 && (
          <Button onClick={() => setPasso(passo - 1)} disabled={enviando}>
            Voltar
          </Button>
        )}
        {passo < 2 && (
          <Button
            color="primary"
            variant="contained"
            onClick={avancar}
            disabled={!!erroParametros}
          >
            Avançar
          </Button>
        )}
        {passo === 2 && (
          <Button
            color="primary"
            variant="contained"
            onClick={confirmar}
            disabled={enviando || carregandoAvisos}
          >
            Confirmar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DialogoGerar;
